import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import '../assets/FamilyDetail.css';

const FamilyDetail = () => {
  const { familyNumber } = useParams();
  const navigate = useNavigate();
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFamilyDetails();
  }, [familyNumber]);

  const fetchFamilyDetails = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getFamilyById(familyNumber);
      setFamily(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching family details:', err);
      setError('Failed to load family details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTuteeClick = (tuteeId) => {
    navigate(`/admin/tutees/${tuteeId}`);
  };

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page (tutees with families tab)
  };

  if (loading) {
    return (
      <div className="family-detail">
        <div className="loading">Loading family details...</div>
      </div>
    );
  }

  if (error || !family) {
    return (
      <div className="family-detail">
        <button className="btn-back-modern" onClick={handleBackClick}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4L6 10l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Families
        </button>
        <div className="error-message">{error || 'Family not found'}</div>
      </div>
    );
  }

  const currentMonthStats = family.monthlyStats && family.monthlyStats.length > 0 
    ? family.monthlyStats[family.monthlyStats.length - 1] 
    : { revenue: 0, lessons: 0, commission: 0 };

  // Calculate 6-month average revenue
  const sixMonthAvgRevenue = family.monthlyStats && family.monthlyStats.length > 0
    ? family.monthlyStats.slice(-6).reduce((sum, stat) => sum + stat.revenue, 0) / Math.min(6, family.monthlyStats.length)
    : 0;

  return (
    <div className="family-detail">
      <button className="btn-back-modern" onClick={handleBackClick}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M12 4L6 10l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Families
      </button>

      <div className="family-detail-header">
        <div className="header-content">
          <h1>{family.familyCode} - {family.parentName}</h1>
          <p className="subtitle">Family Overview and Analytics</p>
        </div>
      </div>

      <div className="family-info-section">
        <div className="info-card">
          <h2>Parent Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Name:</span>
              <span className="info-value">{family.parentName || 'Not provided'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Phone:</span>
              <span className="info-value">{family.parentPhone || 'Not provided'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{family.parentEmail || 'Not provided'}</span>
            </div>
            <div className="info-item full-width">
              <span className="info-label">Address:</span>
              <span className="info-value">{family.parentAddress || 'Not provided'}</span>
            </div>
          </div>
        </div>

        <div className="stats-card">
          <h2>Family Statistics</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{family.totalTutees}</div>
              <div className="stat-label">Children</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{family.totalActiveSubjects}</div>
              <div className="stat-label">Active Subjects</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">${sixMonthAvgRevenue.toFixed(2)}</div>
              <div className="stat-label">Avg Monthly Revenue (6-mo)</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{currentMonthStats.lessons}</div>
              <div className="stat-label">Lessons This Month</div>
            </div>
          </div>
        </div>
      </div>

      <div className="tutees-section">
        <h2>Children in Family</h2>
        <div className="tutees-list">
          {family.tutees.map(tutee => (
            <div 
              key={tutee._id} 
              className="tutee-detail-card"
              onClick={() => handleTuteeClick(tutee._id)}
            >
              <div className="tutee-header">
                <div className="tutee-name-section">
                  <h3>{tutee.name}</h3>
                  <span className="tutee-code">{tutee.code}</span>
                </div>
                <div className="tutee-level">{tutee.level} - Year {tutee.year}</div>
              </div>

              <div className="tutee-info">
                <div className="info-row">
                  <span className="label">School:</span>
                  <span className="value">{tutee.school || 'Not specified'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Subjects:</span>
                  <span className="value">{tutee.subjects.length} total ({tutee.subjects.filter(s => s.isActive).length} active)</span>
                </div>
              </div>

              {tutee.subjects && tutee.subjects.length > 0 && (
                <div className="subjects-section">
                  <div className="subjects-header">
                    <span className="col-subject">Subject</span>
                    <span className="col-mode">Mode</span>
                    <span className="col-rate">Rate</span>
                    <span className="col-tutor">Tutor</span>
                  </div>
                  <div className="subjects-list">
                    {tutee.subjects.filter(s => s.isActive).map((subject, idx) => (
                      <div key={idx} className="subject-row">
                        <span className="col-subject">{subject.name}</span>
                        <span className="col-mode">{subject.mode}</span>
                        <span className="col-rate">${subject.rate}/hr</span>
                        <span className="col-tutor">
                          {subject.assignedTutors && subject.assignedTutors.length > 0
                            ? subject.assignedTutors.map(at => at.tutor?.name || 'Unknown').join(', ')
                            : 'Unassigned'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button className="view-tutee-btn">View Full Profile →</button>
            </div>
          ))}
        </div>
      </div>

      {family.monthlyStats && family.monthlyStats.length > 0 && (
        <div className="revenue-section">
          <h2>Revenue History (Last 6 Months)</h2>
          <div className="revenue-chart">
            {family.monthlyStats.slice(-6).map((stat, idx) => {
              const maxRevenue = Math.max(...family.monthlyStats.slice(-6).map(s => s.revenue));
              const height = maxRevenue > 0 ? (stat.revenue / maxRevenue) * 100 : 0;
              
              return (
                <div key={idx} className="chart-bar">
                  <div className="bar-container">
                    <div 
                      className="bar" 
                      style={{ height: `${height}%` }}
                      title={`Revenue: $${stat.revenue.toFixed(2)}\nLessons: ${stat.lessons}\nCommission: $${stat.commission.toFixed(2)}`}
                    >
                      <span className="bar-value">${stat.revenue.toFixed(0)}</span>
                    </div>
                  </div>
                  <div className="bar-label">
                    {stat.month.split(' ')[0].substring(0, 3)}
                  </div>
                  <div className="bar-details">
                    <div>{stat.lessons} lessons</div>
                    <div>${stat.commission.toFixed(0)} comm.</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyDetail;
